<script setup lang="ts">
import ArrowRight from '~icons/mdi/arrow-right'
import IconEye from '~icons/mdi/eye'
import IconEyeOff from '~icons/mdi/eye-off'
import type { SupportedCountry } from '~/index'

definePageMeta({
  layout: 'auth',
  title: 'registration.label',
  isPublicPage: true,
  isAuthPage: true,
})

const { $snackbar } = useNuxtApp()
const { t } = useI18n()
const store = useUserStore()
const featureSettingsStore = useFeatureSettingsStore()

const localePath = useLocalePath()
const display = useDisplay()

// data
const isUserUnconfirmed = ref(false)
const isLoading = ref(false)
const clinicName = ref()
const streetName = ref()
const streetNumber = ref()
const city = ref()
const billingStreetName = ref()
const billingStreetNumber = ref()
const billingPostalCode = ref()
const billingCity = ref()
const postalCode = ref()
const clinicChainMembershipSelfFilled = ref()
const officialClinicNumber = ref()
const academicTitle = ref()
const firstname = ref()
const lastname = ref()
const vetDiplomaNumber = ref()
const phone = ref()
const username = ref()
const password = ref()
const passwordVisible = ref(false)
const hearAboutUs = ref()
const hearAboutUsText = ref()
const acceptAgb = ref()
const acceptNewsletter = ref()
const sameBillingAddress = ref(true)

// steps
const step = ref(1)
const formStep1Ref = ref()
const formStep2Ref = ref()
const formStep3Ref = ref()

// Validation
const isStep1Valid = ref(false)
const isStep2Valid = ref(false)
const isStep3Valid = ref(false)

// error after submitting the form
const emailError = ref(false)

const clinicNameRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 127),
]
const streetNameRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 63),
]
const streetNumberRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 9),
]

const cityRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 63),
]

const postalCodeRules = [
  useRequiredRule,
  (value: string) => useMinLengthRule(value, 5),
  (value: string) => useMaxLengthRule(value, 5),
  useNumericRule,
]

const billingStreetNameRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 63),
]

const billingStreetNumberRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 9),
]

const billingCityRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 63),
]

const firstnameRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 127),
]
const lastnameRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 127),
]
const phoneRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 63),
]
const emailRules = [
  useRequiredRule,
  (value: string) => useMaxLengthRule(value, 127),
  useEmailRule,
]
const vetDiplomaNumberRules = [
  (value: string) => (value?.length) ? useMaxLengthRule(value, 127) : true,
]
const officialClinicNumberRules = [
  (value: string) => (value?.length) ? useMaxLengthRule(value, 127) : true,
]

const passwordRules = [
  useRequiredRule,
  usePasswordRule,
]

const clinicChainMembershipRules = [
  useRequiredRule,
]

const errorMessages: {
  [key: string]: Array<string>
} = reactive({
  clinicName: [],
  streetName: [],
  streetNumber: [],
  postalCode: [],
  city: [],
  billingStreetName: [],
  billingStreetNumber: [],
  billingPostalCode: [],
  billingCity: [],
  academicTitle: [],
  firstname: [],
  lastname: [],
  phone: [],
  username: [],
  password: [],
  acceptAgb: [],
  acceptNewsletter: [],
  clinicChainMembershipSelfFilled: [],
})

const submitSucceed = ref(false)

const countryCookie = useCookie('vetmazing-country')
const { country } = useRuntimeConfig().public
const { baseURL, mainDomain } = useCountryConfig()
const vetmazingCountries = ref(
  (Object.keys(useAppConfig().countryConfig) as SupportedCountry[])
    .map(countryCode => ({ ...useCountryConfig(countryCode), countryCode }))
    .filter(country => !country.disabled && country.mainDomain === mainDomain)
    // This is a hack to ensure that we only login against the locale system on local!
    .filter(country => baseURL !== '' || country.baseURL === baseURL)
    .map(({ countryCode, localName }) => ({
      value: countryCode,
      title: localName,
      selected: countryCode === country,
    })),
)
const countryCode = ref(vetmazingCountries.value?.find(c => c.selected)?.value ?? country)
const feature = computed(() => useCountryConfig(countryCode.value).feature)
watch(() => countryCode.value, (code) => {
  const cookie = useCookie('vetmazing-country')
  cookie.value = code
}, { immediate: true })

async function submit() {
  for (const key of Object.keys(errorMessages))
    errorMessages[key] = []

  nextTick(async () => {
    isLoading.value = true
    countryCookie.value = countryCode.value

    const clinicChainValue = clinicChainMembershipSelfFilled.value === t('registration.clinicChain.noAffiliation')
      ? null
      : clinicChainMembershipSelfFilled.value

    const response = await store.register({
      clinicName: clinicName.value,
      streetName: streetName.value,
      streetNumber: streetNumber.value,
      postalCode: postalCode.value,
      city: city.value,
      billingStreetName: billingStreetName.value,
      billingStreetNumber: billingStreetNumber.value,
      billingPostalCode: billingPostalCode.value,
      billingCity: billingCity.value,
      clinicChainMembershipSelfFilled: clinicChainValue,
      officialClinicNumber: officialClinicNumber.value,
      academicTitle: academicTitle.value,
      firstname: firstname.value,
      lastname: lastname.value,
      vetDiplomaNumber: vetDiplomaNumber.value,
      phone: phone.value,
      username: username.value,
      password: password.value,
      countrycode: countryCode.value,
      hearAboutUs: hearAboutUs.value,
      hearAboutUsText: hearAboutUsText.value,
      acceptAgb: acceptAgb.value,
      acceptNewsletter: acceptNewsletter.value,
    })
    if (response instanceof Error) {
      if (response.statusCode === 400) {
        // if the email is already taken
        emailError.value = true
        step.value = 1
      }
      if (response.statusCode === 500) {
        submitSucceed.value = false
        isLoading.value = false
        return
      }
      if (response.data && response.data.errors) {
        submitSucceed.value = false
        for (const key of Object.keys(response.data.errors))
          errorMessages[key] = [t(`errors.code.${response.data.errors[key]}`)]
      }
    }
    else {
      submitSucceed.value = true
      if (response === 'UNCONFIRMED') {
        isUserUnconfirmed.value = true
      }
      else {
        $snackbar.showMessage({ text: t('registration.success.confirmed'), color: 'success', closeText: t('close') })
        if (countryCode.value === country)
          return navigateTo(localePath('/welcome'))
        else
          location.href = useCountryConfig(countryCode.value) + localePath('/welcome')
      }
    }

    isLoading.value = false
    scrollToTop()
  })
}

onBeforeRouteLeave(() => {
  if (submitSucceed.value === true)
    return true

  const answer = window.confirm(
    t('discardChanges'),
  )
  // cancel the navigation and stay on the same page
  if (!answer)
    return false
})

function syncBillingAddress() {
  if (sameBillingAddress.value) {
    billingStreetName.value = streetName.value
    billingStreetNumber.value = streetNumber.value
    billingPostalCode.value = postalCode.value
    billingCity.value = city.value
  }
}
watch([streetName, streetNumber, postalCode, city], syncBillingAddress)
watch(sameBillingAddress, (newValue) => {
  if (newValue) {
    syncBillingAddress()
  }
  else {
    billingStreetName.value = ''
    billingStreetNumber.value = ''
    billingPostalCode.value = ''
    billingCity.value = ''
  }
})

async function validateStep(step: number) {
  if (step === 1 && formStep1Ref.value) {
    isStep1Valid.value = await formStep1Ref.value.validate()
    return isStep1Valid.value
  }
  else if (step === 2 && formStep2Ref.value) {
    isStep2Valid.value = await formStep2Ref.value.validate()
    return isStep2Valid.value
  }
  else if (step === 3 && formStep3Ref.value) {
    isStep3Valid.value = await formStep3Ref.value.validate()
    return isStep3Valid.value
  }
  return false
}

async function nextStep() {
  if (isLoading.value) {
    return
  }
  const isValid = await validateStep(step.value)
  if (isValid && step.value < 3) {
    step.value++
  }
  scrollToTop()
}

function prevStep() {
  if (step.value > 1) {
    step.value -= 1
  }
  scrollToTop()
}

const isStepValid = computed(() => {
  if (step.value === 1) {
    return isStep1Valid.value
  }
  else if (step.value === 2) {
    return isStep2Valid.value
  }
  else if (step.value === 3) {
    return isStep3Valid.value
  }
  return false
})

const isMobile = computed(() => {
  return display.smAndDown.value
})

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function handleEmailInput() {
  errorMessages.username = []
  emailError.value = false
}
</script>

<template>
  <v-container>
    <div class="d-flex justify-center align-center mt-8">
      <!-- thank you msg -->
      <div v-if="isUserUnconfirmed" class="d-flex justify-center align-center py-16">
        <v-card
          elevation="0"
          class="thanks--container rounded-xl pa-16 d-flex flex-column justify-center"
          max-width="820"
          min-height="520"
        >
          <h2 class="mb-4 text-h3 text-center">
            {{ $t('thanks') }}
          </h2>
          <p class="text-h6">
            {{ $t('registration.success.unconfirmed') }}
          </p>

          <div>
            <v-btn
              variant="text"
              :to="localePath('/welcome')"
              class="text-decoration-underline pa-0"
              color="red-600"
            >
              <p class="text-h6">
                {{ $t('registration.backToHome') }}
              </p>
            </v-btn>
          </div>

          <v-container class="pa-0">
            <v-row justify="end" align="end">
              <v-col cols="auto" class="pa-6">
                <nuxt-picture
                  src="/images/bird.svg"
                  class="mr-16"
                  width="98"
                  height="auto"
                  style="position: absolute; bottom: 0; right: 0; transform: scaleX(-1);"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </div>

      <template v-else>
        <v-card
          elevation="0"
          class="rounded-xl"
          cols="12"
          md="6"
          width="820"
        >
          <v-stepper v-model="step" :mobile="isMobile" flat>
            <v-stepper-header class="d-flex justify-center mt-6 elevation-0">
              <v-stepper-item
                :complete="step > 1"
                :error="emailError"
                :title="$t('registration.stepper.account')"
                :value="1"
                class="text-body-1"
                selected-class="font-weight-bold text-red-600"
              />
              <v-stepper-item
                :complete="step > 2"
                :title="$t('registration.stepper.clinicDetails')"
                :value="2"
                class="text-body-1"
                selected-class="font-weight-bold text-red-600"
              />
              <v-stepper-item
                :complete="step > 3"
                :title="$t('registration.stepper.deliveryAddress')"
                :value="3"
                class="text-body-1"
                selected-class="font-weight-bold text-red-600"
              />
            </v-stepper-header>

            <v-stepper-window v-show="step === 1" value="1">
              <v-form ref="formStep1Ref" v-model="isStep1Valid" class="d-flex justify-center">
                <v-col cols="12" md="8" class="pa-0">
                  <v-col cols="12" class="text-h3 font-weight-bold text-center pa-0 mb-4">
                    {{ $t('registration.header') }}
                  </v-col>

                  <v-col cols="12">
                    <h2>
                      {{ $t('registration.subline') }}
                    </h2>
                    <v-row class="d-flex align-center ma-0 py-4">
                      <h2 class="mr-2">
                        {{ $t('registration.login') }}
                      </h2>
                      <v-btn
                        variant="text"
                        :to="localePath('/login')"
                        class="text-decoration-underline font-weight-bold pa-0"
                        color="red-600"
                      >
                        <h2>
                          {{ $t('login.label') }}
                        </h2>
                      </v-btn>
                    </v-row>
                  </v-col>

                  <v-col class="mt-4">
                    <h2 class="mb-2 font-weight-bold">
                      {{ $t("registration.clinic.title") }}
                    </h2>

                    <v-label :text="$t('registration.clinic.placeholder')" />
                    <v-text-field-primary
                      v-model="clinicName"
                      :rules="clinicNameRules"
                      :error-messages="errorMessages.clinicName"
                    />

                    <h2 class="mb-2 font-weight-bold">
                      {{ $t("registration.loginDetails") }}
                    </h2>

                    <v-label :text="$t('registration.clinic.email')" />
                    <v-text-field-primary
                      v-model="username"
                      :rules="emailRules"
                      :error-messages="errorMessages.username"
                      @input="handleEmailInput"
                    />

                    <v-label :text="$t('registration.clinic.password')" />
                    <v-text-field-primary
                      v-model="password"
                      :rules="passwordRules"
                      :append-inner-icon="passwordVisible ? IconEyeOff : IconEye"
                      :type="passwordVisible ? 'text' : 'password'"
                      :error-messages="errorMessages.password"
                      @click:append-inner="() => (passwordVisible = !passwordVisible)"
                    />

                    <template v-if="vetmazingCountries && vetmazingCountries.length > 1">
                      <v-label :text="$t('registration.address.country')" />
                      <v-select v-model="countryCode" :items="vetmazingCountries" />
                    </template>

                    <v-row class="d-flex justify-center">
                      <v-col cols="12" sm="3" class="d-flex justify-center">
                        <v-btn-primary
                          variant="outlined"
                          color="green-500"
                          class="my-2 font-weight-bold"
                          :disabled="!isStepValid"
                          @click="nextStep"
                        >
                          {{ $t('registration.next') }}
                          <v-icon :icon="ArrowRight" color="green-500 mx-1" />
                        </v-btn-primary>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-col>
              </v-form>
            </v-stepper-window>

            <v-stepper-window v-show="step === 2" value="2">
              <v-form ref="formStep2Ref" v-model="isStep2Valid" class="d-flex justify-center">
                <v-col cols="12" md="8">
                  <h2 class="mb-2 font-weight-bold">
                    {{ $t("profile.clinic.title") }}
                  </h2>

                  <v-label :text="$t('registration.clinic.phone')" />
                  <v-text-field-primary v-model="phone" :rules="phoneRules" />

                  <div v-if="feature.displayOfficialClinicNumber">
                    <v-label :text="$t('registration.officialClinicNumber')" />
                    <v-text-field-primary v-model="officialClinicNumber" :rules="officialClinicNumberRules" />
                  </div>

                  <div v-if="featureSettingsStore.settings?.registration?.clinicChains && featureSettingsStore.settings?.registration?.clinicChains.length > 0">
                    <v-label :text="$t('registration.clinicChainMembershipSelfFilled')+ ' *'" />
                    <v-select
                      v-model="clinicChainMembershipSelfFilled"
                      :items="[$t('registration.clinicChain.noAffiliation'), ...featureSettingsStore.settings?.registration?.clinicChains]"
                      bg-color="white"
                      focused
                      :rules="clinicChainMembershipRules"
                      :error-messages="errorMessages.clinicChainMembershipSelfFilled"
                      :placeholder="$t('profile.clinic.selectGpo')"
                      :hint="$t('profile.clinic.hint')"
                      persistent-hint
                    />
                  </div>

                  <v-spacer class="mt-12" />

                  <h2 class="mb-4 font-weight-bold">
                    {{ $t('profile.medical.title') }}
                  </h2>

                  <v-label :text="$t('registration.firstname')" />
                  <v-text-field-primary
                    v-model="firstname"
                    :rules="firstnameRules"
                    :error-messages="errorMessages.firstname"
                  />

                  <v-label :text="$t('registration.lastname')" />
                  <v-text-field-primary
                    v-model="lastname"
                    :rules="lastnameRules"
                    :error-messages="errorMessages.lastname"
                  />

                  <div v-if="feature.displayVetDiplomaNumber">
                    <v-label :text="$t('profile.medical.dpe')" />
                    <v-text-field-primary v-model="vetDiplomaNumber" :rules="vetDiplomaNumberRules" />
                  </div>

                  <v-row class="d-flex justify-center align-center">
                    <v-col cols="6" sm="3">
                      <v-btn-primary
                        variant="text"
                        color="green-500"
                        class="font-weight-bold"
                        @click="prevStep"
                      >
                        {{ $t('registration.previous') }}
                      </v-btn-primary>
                    </v-col>
                    <v-col cols="6" sm="3">
                      <v-btn-primary
                        variant="outlined"
                        color="green-500"
                        class="my-2 font-weight-bold"
                        :disabled="!isStepValid"
                        @click="nextStep"
                      >
                        {{ $t('registration.next') }}
                        <v-icon :icon="ArrowRight" color="green-500 mx-1" />
                      </v-btn-primary>
                    </v-col>
                  </v-row>
                </v-col>
              </v-form>
            </v-stepper-window>

            <v-stepper-window v-show="step === 3" value="3">
              <v-form ref="formStep3Ref" v-model="isStep3Valid" class="justify-center d-flex align-center">
                <v-col cols="12" md="8">
                  <h2 class="mb-4 font-weight-bold">
                    {{ $t("registration.address.title") }}
                  </h2>

                  <v-label :text="$t('registration.address.streetName')" />
                  <v-text-field-primary
                    v-model="streetName"
                    :rules="streetNameRules"
                    :error-messages="errorMessages.streetName"
                  />
                  <v-label :text="$t('registration.address.streetNumber')" />
                  <v-text-field-primary
                    v-model="streetNumber"
                    :rules="streetNumberRules"
                    :error-messages="errorMessages.streetNumber"
                  />

                  <v-label :text="$t('registration.address.postalCode')" />
                  <v-text-field-primary
                    v-model="postalCode"
                    :rules="postalCodeRules"
                    :error-messages="errorMessages.postalCode"
                  />

                  <v-label :text="$t('registration.address.city')" />
                  <v-text-field-primary v-model="city" :rules="cityRules" :error-messages="errorMessages.city" />

                  <v-checkbox
                    v-model="sameBillingAddress"
                    :label="$t('profile.billing.same')"
                    hide-details
                    color="red-600"
                  />

                  <v-divider v-if="sameBillingAddress" class="pa-1" />

                  <div v-if="!sameBillingAddress">
                    <h2 class="mb-4 mt-4 font-weight-bold">
                      {{ $t("profile.billing.title") }}
                    </h2>

                    <v-label :text="$t('registration.address.streetName')" />
                    <v-text-field-primary v-model="billingStreetName" :rules="billingStreetNameRules" />

                    <v-label :text="$t('registration.address.streetNumber')" />
                    <v-text-field-primary v-model="billingStreetNumber" :rules="billingStreetNumberRules" />

                    <v-label :text="$t('registration.address.postalCode')" />
                    <v-text-field-primary v-model="billingPostalCode" :rules="postalCodeRules" />

                    <v-label :text="$t('registration.address.city')" />
                    <v-text-field-primary v-model="billingCity" :rules="billingCityRules" />
                  </div>

                  <v-checkbox v-model="acceptNewsletter" :label="$t('registration.acceptNewsletter')" hide-details />
                  <v-checkbox v-model="acceptAgb" :rules="[useRequiredRule]" :error-messages="errorMessages.acceptAgb">
                    <template #label>
                      <i18n-t keypath="registration.acceptAgb.text" tag="div">
                        <template #agbLink>
                          <nuxt-link target="_blank" :to="localePath('/agb')">
                            {{ $t('layout.footer.agb') }}
                          </nuxt-link>
                        </template>
                        <template #datasecurityLink>
                          <nuxt-link target="_blank" :to="localePath('/datasecurity')">
                            {{ $t('registration.acceptAgb.datasecurity') }}
                          </nuxt-link>
                        </template>
                      </i18n-t>
                    </template>
                  </v-checkbox>

                  <v-row class="d-flex justify-center align-center">
                    <v-col cols="6" sm="3">
                      <v-btn-primary variant="text" color="green-500 font-weight-bold" @click="prevStep">
                        {{ $t('registration.previous') }}
                      </v-btn-primary>
                    </v-col>
                    <v-col cols="4" sm="4">
                      <v-btn-primary
                        class="bg-green-500 font-weight-bold"
                        :disabled="!isStepValid"
                        :loading="isLoading"
                        @click="submit"
                      >
                        {{ $t('register') }}
                      </v-btn-primary>
                    </v-col>
                  </v-row>
                </v-col>
              </v-form>
            </v-stepper-window>
          </v-stepper>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<style lang="scss">
// stepper error
.v-stepper-item--error .v-stepper-item__avatar {
  border-color: rgb(var(--v-theme-red-600)) !important;
  color: rgb(var(--v-theme-red-600)) !important;
}

// stepper error text
.v-stepper-item--error .v-stepper-item__title {
  color: rgb(var(--v-theme-error)) !important;
  font-weight: bold;
  background-color: transparent;
}

.v-stepper-item__avatar.v-avatar {
  background-color: transparent;
  border: 2px solid rgb(var(--v-theme-heiland-grey));
  color: rgb(var(--v-theme-heiland-grey));
}

.v-stepper-item--complete .v-stepper-item__avatar.v-avatar {
  background-color: transparent;
  border: 2px solid rgb(var(--v-theme-green-700));
  color: rgb(var(--v-theme-green-700));
  font-weight: bold;
}

// completed text color
.v-stepper-item--complete .v-stepper-item__title {
  color: rgb(var(--v-theme-green-700));
  font-weight: bold;
  background-color: transparent;
}

.v-stepper-item--selected .v-stepper-item__avatar.v-avatar {
  background-color: transparent;
  border: 2px solid rgb(var(--v-theme-red-600));
  color: rgb(var(--v-theme-red-600));
}
</style>
